// 快斗 Blitz Duel — client half (TECH_SPEC §4bis.28), with no DOM and no
// network attached. Same logic/DOM split as `peak.ts` vs `ui/`: everything that
// DECIDES lives here so the client test suite can pin it without a browser;
// `game/duelIntro.ts` and `session.ts` only ever apply these answers.
//
// Three independent things:
//
//   1. `duelEnabled` — the dark-rollout gate, a clone of §4bis.22's
//      `peakEnabled` under its own salt. At `DUEL_PCT=0` nobody is in and
//      rendering is pixel-identical to today — a tested property.
//   2. `duelOutcomeFor` — the verdict, judged by SURVIVAL and never by the
//      score comparator. A duel's distance is not a score.
//   3. The pinned presentation tables: countdown length, CTA pool per outcome,
//      and when a link may open as a duel at all.

import {
  DUEL_MERCY_FRAMES,
  DUEL_MIN_SOURCE_DEATH,
  DUEL_PREFIX_CAP,
  MAX_INPUT_EVENTS,
  duelSuffixBudget,
  handoffMinBudget,
} from '@beatme/sim';

import { hashString, mix32 } from './rng';
import type { EntryType, SliceModeWire } from './types';

/**
 * Rollout salt. Deliberately NOT `card-ab-v1`, `rival-ladder-v1`, `peak-v1` or
 * `fair-v1`: the same device id under a different salt is an *independent*
 * assignment, so being in the duel arm tells you nothing about the other four
 * experiments. Pinned by a golden vector in the tests, on both sides.
 */
export const DUEL_ID = 'duel-v1';

/** Stable bucket 0–99 for a device: `mix32(fnv1a("duel-v1:" + id)) % 100`. */
export function duelBucket(deviceId: string): number {
  return mix32(hashString(`${DUEL_ID}:${deviceId}`)) % 100;
}

/**
 * Is this device in the duel arm? `pct` is the server's `duelPct` (0 = dark,
 * 100 = everyone). Anything unparseable is 0: fail closed on both wire ends.
 */
export function duelEnabled(deviceId: string, pct: number | undefined | null): boolean {
  if (typeof pct !== 'number' || !Number.isFinite(pct) || pct <= 0) return false;
  if (pct >= 100) return true;
  return duelBucket(deviceId) < pct;
}

// ---------------------------------------------------------------------------
// 帮我 The hand-back (§4bis.31) — the second verb, on its own dial
// ---------------------------------------------------------------------------

/**
 * The SIXTH rollout salt, independent of `card-ab-v1`, `rival-ladder-v1`,
 * `peak-v1`, `fair-v1` and `duel-v1` by construction. Pinned by a golden vector
 * on both sides.
 *
 * It needs to be its own dial rather than riding `DUEL_PCT` because **T2 is the
 * metric both 004 and 005 are judged on**: a second-hop lift under one flag
 * could not be attributed between "the run continues" (005) and "there is a
 * second verb" (006). The off-cell is not dead either — *duel on, handoff off*
 * is exactly what 005 shipped, so it is a real control.
 */
export const HANDOFF_ID = 'handoff-v1';

export function handoffBucket(deviceId: string): number {
  return mix32(hashString(`${HANDOFF_ID}:${deviceId}`)) % 100;
}

/** Is this device offered the second verb? Nested inside the duel arm by the
 *  caller — a hand-back IS a duel, so there is no handoff-on/duel-off cell. */
export function handoffEnabled(deviceId: string, pct: number | undefined | null): boolean {
  if (typeof pct !== 'number' || !Number.isFinite(pct) || pct <= 0) return false;
  if (pct >= 100) return true;
  return handoffBucket(deviceId) < pct;
}

/**
 * May the run that just ended be handed on? **Only the author of the current
 * frontier may pass it on** — the client's mirror of the server's
 * `handoffEligible`, kept here so the button is never offered for a submit the
 * server would refuse.
 *
 * `endFrame` is where the run really stopped: the death frame, or the frame it
 * reached alive. `slice` is the duel this run played, or null for a full run.
 *
 *   - full run: eligible once it died past `DUEL_MIN_SOURCE_DEATH`. That is
 *     ~40% of deaths, which is deliberately WIDER than the literal ask and is
 *     exactly the owner's words: when you die you can ask others to challenge
 *     you OR to help you.
 *   - duel row: eligible exactly when it PASSED the line. Handing on a run that
 *     fell short would ratchet a shared frontier backward.
 *
 * A run that never died (finished, or ran the track out) is NOT eligible: the
 * volley ends at the finish rather than looping there.
 */
export function handoffEligible(
  deathFrame: number,
  slice: DuelSlice | null | undefined,
): boolean {
  if (deathFrame < 0) return false; // finished or still alive — nothing to hand on
  if (deathFrame < DUEL_MIN_SOURCE_DEATH) return false;
  return slice ? deathFrame >= slice.winFrame : true;
}

/** Is THIS run a hand-back (as opposed to a challenge)? §4bis.31: identified
 *  solely by the slice's mode, which the server derived. */
export function isHandoff(slice: DuelSlice | null | undefined): boolean {
  return slice?.mode === 'handoff';
}

/**
 * Which verb the SEAL's single CTA carries (§4bis.31 §2).
 *
 * `PeakOverlay` has exactly one call to action, and on a sealed duel the seal
 * is the FIRST screen shown — so whatever that CTA is, is the verb most players
 * will ever meet. Leaving it as 回敬 put 帮我 one dismissal behind: a player
 * who takes the ask in front of them never sees the second verb at all, which
 * inverts the precedence the spec fixes and hides the verb on precisely the run
 * the owner's directive is about (the one that just passed the grave).
 *
 * The other verb is not lost — dismissing the seal lands on the duel result
 * screen, which offers both. What this decides is which one LEADS.
 */
export function sealVerb(handoffOffered: boolean): 'challenge' | 'handoff' {
  return handoffOffered ? 'handoff' : 'challenge';
}

/**
 * Which stamp pool a DEATH screen draws from (§4bis.31).
 *
 * The default pools carry the plunder voice — 「你从 {name} 手里多抢了 {d} 米」
 * / "grabbed out of their hands" — which is right for 打败我 and actively wrong
 * on the one screen whose primary action is a gift. A screen cannot say "I took
 * yours" and "here, have this" in the same frame.
 *
 * `fallback` is whatever that surface would have drawn: `pool.win` and friends
 * on a full run, `duelStampPool(outcome)` on a duel. Swapping only the POOL
 * NAME — never the seeded stream or the draw position — is what keeps the
 * Layer-3 baselines deterministic across this change.
 */
export function deathStampPool(handoffOffered: boolean, fallback: string): string {
  return handoffOffered ? 'pool.handoffStamp' : fallback;
}

// ---------------------------------------------------------------------------
// The result screen's button slots (§4bis.31 verb precedence)
// ---------------------------------------------------------------------------

/**
 * What a slot holds. Deliberately abstract: this module DECIDES the
 * arrangement, `session.ts` supplies the labels and handlers, so the rule can
 * be pinned without a DOM.
 */
export type SlotKind = 'handoff' | 'share' | 'shareAlt' | 'again';

export interface SlotPlan {
  primary: SlotKind;
  secondary: SlotKind | null;
  extras: SlotKind[];
}

/**
 * Arrange the full-run death screen's buttons (§4bis.31 §2, design §4.4).
 *
 * Three rules meet here, and two of them used to contradict each other:
 *
 *   1. **Design §4.4** fixes primary/secondary per outcome; extras are
 *      low-emphasis.
 *   2. **§4bis.21's ladder rule** drops the extra 再来一次, because rung 1 IS a
 *      rematch against the same opponent and two controls for one action read
 *      as two different actions.
 *   3. **§4bis.31's verb precedence** promotes 帮我 into `primary` whenever the
 *      run is hand-backable, demoting whatever led before.
 *
 * Rules 2 and 3 intersect badly if written independently. On the `tie`/`lose`
 * branches the base primary IS 再来一次, so promoting 帮我 evicts it from its
 * own slot; with the ladder also on, rule 2 then refused to restore it as an
 * extra, and **the retry vanished from the screen entirely**. Neither flag's
 * own tests could see that, because it needs both arms at once.
 *
 * The resolution: rule 2 exists to remove a DUPLICATE. When the hand-back is
 * what displaced the retry, there is no longer a duplicate to remove, so the
 * retry comes back as an extra even under the ladder. Stated as one invariant:
 * **再来一次 is always reachable unless the ladder is offering the rematch
 * itself.**
 */
export function resultSlots(opts: {
  basePrimary: SlotKind;
  baseSecondary: SlotKind | null;
  ladder: boolean;
  handoff: boolean;
}): SlotPlan {
  let primary = opts.basePrimary;
  let secondary = opts.baseSecondary;

  if (opts.handoff) {
    // The old leader steps down one slot — unless it was the retry, which is
    // not a verb and must not displace the challenge share.
    secondary = primary === 'again' ? secondary : primary;
    primary = 'handoff';
  }

  const extras: SlotKind[] = [];
  const retryHasASlot = primary === 'again' || secondary === 'again';
  const displacedByHandoff = opts.handoff && !retryHasASlot;
  if (!retryHasASlot && (!opts.ladder || displacedByHandoff)) extras.push('again');

  return { primary, secondary, extras };
}

// ---------------------------------------------------------------------------
// The slice, as the client receives it
// ---------------------------------------------------------------------------

/** The duel block on the boot payload (§4bis.28). Server-derived, always. */
export interface DuelSlice {
  sourceRunId: string;
  /** §4bis.31: which verb minted this slice. Absent on an old server ⇒ every
   *  link was a challenge, which is exactly what it was. */
  mode?: SliceModeWire;
  startFrame: number;
  /**
   * The line to PASS. §4bis.30: not where the run stops — the 活过了 seal fires
   * here mid-run and the player keeps going until they actually die.
   */
  winFrame: number;
  sourceEndFrame: number;
  /** Absolute track meters at the handover — the HUD reads absolute. */
  startM: number;
  /** Absolute track meters the sender reached: their grave. */
  sourceEndM: number;
  /**
   * §4bis.30: how many of the source's events the prefix replays. The
   * challenger's own input budget is `MAX_INPUT_EVENTS − prefixCount`, and the
   * server enforces exactly that number — so the client must have it, or it
   * could record a run its own server would 422. Optional on the wire for
   * tolerance (types.ts's rule); absent falls back to the worst legal prefix.
   */
  prefixCount?: number;
}

/**
 * How many events this run may record (§4bis.30). A full run gets
 * `MAX_INPUT_EVENTS`; a duel gets what the prefix left over.
 *
 * The fallback when an older server sends no `prefixCount` is `DUEL_PREFIX_CAP`
 * — the WORST prefix a slice may legally have. Assuming 0 would be the
 * optimistic answer and could let the client record a stream its own server
 * refuses; assuming the cap can only ever under-spend, and 600 − 150 = 450
 * events is still far more than any measured run uses (max 44 on five seeds).
 */
export function duelInputBudget(slice: DuelSlice | null): number {
  if (!slice) return MAX_INPUT_EVENTS;
  if (slice.prefixCount !== undefined) return duelSuffixBudget(slice.prefixCount);
  // No `prefixCount` on the wire — an older server. The fallback must be the
  // worst LEGAL prefix for this verb, because assuming better could let the
  // client record a stream its own server refuses.
  //
  // §4bis.31: the two verbs have different worst cases, and using the
  // challenge's for a hand-back would be catastrophically wrong in the safe
  // direction — `DUEL_PREFIX_CAP = 150` would promise 450 events where the
  // guard only guarantees `handoffMinBudget(startFrame)`. So a hand-back falls
  // back to exactly what its own mint guard guarantees, which is the largest
  // number that is still certainly true.
  if (slice.mode === 'handoff') return handoffMinBudget(slice.startFrame);
  return duelSuffixBudget(DUEL_PREFIX_CAP);
}

/**
 * May this entry open as a duel? The §4bis.28 entry matrix, in one predicate:
 *
 *   link  — YES by default, when the boot carries a slice
 *   rerun — YES ("can I survive where I died?"), the sharer's own F3 relief
 *   first — NO. A cold device with no link meets the game from 0 m; the
 *           tutorial is untouchable.
 *   walkin— NO. There is no source run to duel.
 */
export function duelAvailable(
  entryType: EntryType,
  slice: DuelSlice | null | undefined,
  armed: boolean,
): slice is DuelSlice {
  if (!armed || !slice) return false;
  return entryType === 'link' || entryType === 'rerun';
}

// ---------------------------------------------------------------------------
// The verdict
// ---------------------------------------------------------------------------

/**
 * `win`     PASSED the line — you outlived their grave by a second. §4bis.30:
 *           still a win if you die two seconds later. The duel asked one
 *           question ("can you outlive where I fell?") and you answered it;
 *           what changes is that the answer is no longer a flat +1 s.
 * `tie`     died at or past their end, but short of the line: 同归.
 * `lose`    died before they did.
 * `instant` died inside the mercy window — a start-line accident, not a duel.
 *           No seal, and the retry does NOT re-attach to the chain.
 */
export type DuelOutcome = 'win' | 'tie' | 'lose' | 'instant';

/**
 * `endFrame` is where the challenger's run really ended: their death frame, or
 * `MAX_FRAMES` if they never died. Judged by survival, never by the §3.2 score
 * comparator: a duel's distance is a place on the track, not a score.
 *
 * Deliberately unchanged by §4bis.30 — it already keys on `endFrame ≥ winFrame`,
 * which is exactly "passed the line", and passing was never the same fact as
 * "was alive when the run stopped".
 */
export function duelOutcomeFor(endFrame: number, slice: DuelSlice): DuelOutcome {
  if (endFrame >= slice.winFrame) return 'win';
  if (endFrame - slice.startFrame < DUEL_MERCY_FRAMES) return 'instant';
  return endFrame >= slice.sourceEndFrame ? 'tie' : 'lose';
}

/** Metres short of the sender's grave — 0 once you are past it. */
export function duelGapM(playerM: number, slice: DuelSlice): number {
  return Math.max(0, slice.sourceEndM - playerM);
}

/**
 * §4bis.30 — the truthful margin, and the number that replaces 004's constant
 * "+1 秒" on the result screen and the share card. Metres PAST the grave, so
 * two winners' cards finally say different things.
 */
export function duelMarginM(playerM: number, slice: DuelSlice): number {
  return Math.max(0, playerM - slice.sourceEndM);
}

/** The same margin in frames, for `duel_result`'s instrumentation. */
export function duelMarginFrames(endFrame: number, slice: DuelSlice): number {
  return endFrame - slice.sourceEndFrame;
}

// ---------------------------------------------------------------------------
// Presentation tables (pinned; zero implementer latitude)
// ---------------------------------------------------------------------------

/** 3 beats × 600 ms, wall-clock, entirely outside the sim. */
export const DUEL_COUNTDOWN_MS = 1800;
/**
 * A device that has never been taught gets longer, because the countdown is
 * also where it reads the gesture legend. A duel never sets
 * `beatme.tutorialDone`, so its next FULL run still gets the stop-frame
 * tutorial — the duel borrows the teaching moment, it does not spend it.
 */
export const DUEL_COUNTDOWN_UNTAUGHT_MS = 3200;

export function duelCountdownMs(taught: boolean): number {
  return taught ? DUEL_COUNTDOWN_MS : DUEL_COUNTDOWN_UNTAUGHT_MS;
}

/** Stamp pool per outcome. `instant` never seals, so it has no stamp. */
export type DuelStampPool = 'pool.duelWin' | 'pool.duelTie' | 'pool.duelLose';

export function duelStampPool(outcome: Exclude<DuelOutcome, 'instant'>): DuelStampPool {
  if (outcome === 'win') return 'pool.duelWin';
  return outcome === 'tie' ? 'pool.duelTie' : 'pool.duelLose';
}

/** Sub-line pool per outcome (the small line under the stamp). */
export type DuelLinePool =
  | 'pool.duelWinLine'
  | 'pool.duelTieLine'
  | 'pool.duelLoseLine'
  | 'pool.duelInstant';

export function duelLinePool(outcome: DuelOutcome): DuelLinePool {
  if (outcome === 'win') return 'pool.duelWinLine';
  if (outcome === 'tie') return 'pool.duelTieLine';
  return outcome === 'instant' ? 'pool.duelInstant' : 'pool.duelLoseLine';
}

/**
 * Does this outcome seal? Everything except an instant death, which gets an
 * instant free retry instead — a seal on a start-line accident would be a
 * trophy for nothing, and the retry is the kinder answer.
 */
export function duelSeals(outcome: DuelOutcome): outcome is Exclude<DuelOutcome, 'instant'> {
  return outcome !== 'instant';
}

/**
 * Does this duel attach to the chain? Only the FIRST duel against a given
 * source: retries and rematches send no `parentRunId`, exactly as ladder rungs
 * do (§4bis.21). Otherwise instant-death retries would inflate the very depth
 * number this iteration is judged on. The server enforces the same rule; this
 * is the client keeping its own promise rather than relying on being caught.
 */
export function duelParentRunId(
  slice: DuelSlice,
  attempt: number,
  entryType: EntryType,
): string | undefined {
  if (attempt > 0) return undefined; // a retry or a 回敬
  return entryType === 'link' ? slice.sourceRunId : undefined;
}
